/**
 * The reviews page's state in the URL: the search, the three chips and both
 * sections' pages (`q`, `status`, `repo`, `author`, `page`, `held`), so a link
 * opens the same view. A search or chip change starts both lists over at page
 * one; page one never shows in the address.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import type { ReviewsQuery } from "./reviewsApi.js";
import type { ReviewsList } from "./useReviewsList.js";

const SEARCH_DEBOUNCE_MS = 250;

function pageParam(raw: string | null): number {
  const page = Number.parseInt(raw ?? "", 10);
  return Number.isFinite(page) && page >= 1 ? page : 1;
}

function lastPage(total: number, pageSize: number): number {
  return Math.max(1, Math.ceil(total / pageSize));
}

export type SetReviewsParams = (updates: Record<string, string>, resetPages: boolean) => void;

export function useReviewsQuery(): {
  query: ReviewsQuery;
  searchParams: URLSearchParams;
  setParams: SetReviewsParams;
} {
  const [searchParams, setSearchParams] = useSearchParams();
  const query: ReviewsQuery = useMemo(
    () => ({
      q: searchParams.get("q") ?? "",
      status: searchParams.get("status") ?? "",
      repo: searchParams.get("repo") ?? "",
      author: searchParams.get("author") ?? "",
      page: pageParam(searchParams.get("page")),
      heldPage: pageParam(searchParams.get("held")),
    }),
    [searchParams],
  );
  const setParams = useCallback<SetReviewsParams>(
    (updates, resetPages) =>
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          for (const [key, value] of Object.entries(updates)) {
            const isPage = key === "page" || key === "held";
            if (value !== "" && !(isPage && value === "1")) next.set(key, value);
            else next.delete(key);
          }
          if (resetPages) {
            next.delete("page");
            next.delete("held");
          }
          return next;
        },
        { replace: true },
      ),
    [setSearchParams],
  );
  return { query, searchParams, setParams };
}

/** The search box's text, written to the URL once the reader stops typing. */
export function useSearchDraft(
  q: string,
  setParams: SetReviewsParams,
): [string, (value: string) => void] {
  const [draft, setDraft] = useState(q);
  useEffect(() => setDraft(q), [q]);
  useEffect(() => {
    if (draft.trim() === q) return;
    const timer = setTimeout(() => setParams({ q: draft.trim() }, true), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [draft, q, setParams]);
  return [draft, setDraft];
}

/**
 * A page past the end (the last held review answered, a stale link) moves
 * back to the last page that has rows.
 */
export function useClampPages(
  list: ReviewsList,
  query: ReviewsQuery,
  setParams: SetReviewsParams,
): void {
  const heldLast = lastPage(list.held.total, list.held.pageSize);
  const restLast = lastPage(list.rest.total, list.rest.pageSize);
  useEffect(() => {
    if (!list.ready || list.failed) return;
    const updates: Record<string, string> = {};
    if (query.heldPage > heldLast) updates.held = String(heldLast);
    if (query.page > restLast) updates.page = String(restLast);
    if (Object.keys(updates).length > 0) setParams(updates, false);
  }, [list.ready, list.failed, heldLast, restLast, query.heldPage, query.page, setParams]);
}
